import { getSlopeCheckCache, getSunPositions, state } from './app';
import {
  addP,
  buildingCornersLm,
  normalizeVec,
  orientedRectCorners,
  rotP,
  scaleVec,
  subP,
} from './geometry';
import { buildingCanvas, buildingCtx, getCanvasDpr, lmToScreen } from './mapView';
import {
  BICYCLE_ZONE_ID,
  PARKING_METHOD_COLORS,
  PARKING_METHOD_KEYS,
  parkingZoneId,
} from './parking/constants';
import { laneAisleRects } from './parkingEngine';
import { computeShadowPolygonLm, hexToRgba, shadowColorForHour } from './shadowCheck';
import type {
  Building,
  DrivewayConnector,
  OrientedRect,
  ParkingLane,
  ParkingSlot,
  Point,
} from './types';

// Canvas overlay rendering: buildings, selection handles, parking/bicycle
// slots and zones, and shadow polygons. redrawBuildingsOverlay() is the
// master draw loop, called after every state change and on every map
// move/zoom/resize.

export const HANDLE_R = 6;
export const HANDLE_HIT_R = 10;
export const ROTATE_HANDLE_OFFSET = 26;

export function getBuildingScreenCorners(b: OrientedRect): Point[] {
  return buildingCornersLm(b).map(lmToScreen);
}

export interface HandleScreenPositions {
  nw: Point;
  ne: Point;
  se: Point;
  sw: Point;
  n: Point;
  e: Point;
  s: Point;
  w: Point;
  rotate: Point;
  corners: Point[];
}
export function getHandleScreenPositions(b: OrientedRect): HandleScreenPositions {
  const corners = getBuildingScreenCorners(b); // nw, ne, se, sw
  const [nw, ne, se, sw] = corners;
  const mid = (p: Point, q: Point): Point => ({ x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 });
  const n = mid(nw, ne);
  const e = mid(ne, se);
  const s = mid(se, sw);
  const w = mid(sw, nw);
  const center = { x: (nw.x + ne.x + se.x + sw.x) / 4, y: (nw.y + ne.y + se.y + sw.y) / 4 };
  const upDir = normalizeVec(subP(n, center));
  const rotate = addP(n, scaleVec(upDir, ROTATE_HANDLE_OFFSET));
  return { nw, ne, se, sw, n, e, s, w, rotate, corners };
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export function drawBuilding(
  ctx: CanvasRenderingContext2D,
  b: Building,
  selected: boolean,
  slopeResult: { overallOk: boolean } | undefined
): void {
  const corners = getBuildingScreenCorners(b);
  const ng = slopeResult && !slopeResult.overallOk;
  ctx.beginPath();
  corners.forEach((c, i) => {
    if (i === 0) ctx.moveTo(c.x, c.y);
    else ctx.lineTo(c.x, c.y);
  });
  ctx.closePath();
  ctx.fillStyle = selected ? 'rgba(232,83,106,0.55)' : 'rgba(232,83,106,0.35)';
  ctx.strokeStyle = ng ? '#dc2626' : selected ? '#a01f38' : '#c0455f';
  ctx.lineWidth = ng ? 3 : selected ? 2.5 : 1.5;
  ctx.fill();
  ctx.stroke();

  const c0 = lmToScreen(b.center);
  const label1 = b.name;
  const label2 = `${b.floors}F`;
  ctx.font = 'bold 12px "Yu Gothic UI",sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const textW = Math.max(ctx.measureText(label1).width, ctx.measureText(label2).width) + 14;
  ctx.fillStyle = 'rgba(32,36,44,0.72)';
  roundRect(ctx, c0.x - textW / 2, c0.y - 17, textW, 34, 6);
  ctx.fill();
  ctx.fillStyle = '#fff';
  ctx.fillText(label1, c0.x, c0.y - 8);
  ctx.fillText(label2, c0.x, c0.y + 8);

  if (selected) drawHandles(ctx, b);
}

export function drawHandles(ctx: CanvasRenderingContext2D, b: OrientedRect): void {
  const h = getHandleScreenPositions(b);
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(h.n.x, h.n.y);
  ctx.lineTo(h.rotate.x, h.rotate.y);
  ctx.strokeStyle = '#888';
  ctx.lineWidth = 1.2;
  ctx.stroke();

  ctx.lineWidth = 1.5;
  (['nw', 'ne', 'se', 'sw', 'n', 'e', 's', 'w'] as const).forEach((k) => {
    const p = h[k];
    ctx.beginPath();
    ctx.arc(p.x, p.y, HANDLE_R, 0, Math.PI * 2);
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.strokeStyle = '#a01f38';
    ctx.stroke();
  });
  ctx.beginPath();
  ctx.arc(h.rotate.x, h.rotate.y, HANDLE_R, 0, Math.PI * 2);
  ctx.fillStyle = 'var(--accent)';
  ctx.fill();
  ctx.strokeStyle = 'var(--accent-dark)';
  ctx.stroke();
  ctx.restore();
}

const PARKING_PAVEMENT = '#8b9096';
const PARKING_COLORS: Record<string, string | null> = {
  normal: null,
  kei: 'rgba(120,200,160,0.6)',
  disabled: 'rgba(66,133,244,0.78)',
  guest: 'rgba(245,158,90,0.75)',
  ev: 'rgba(155,109,224,0.75)',
};

function fillScreenPoly(ctx: CanvasRenderingContext2D, corners: Point[], fillStyle: string): void {
  ctx.beginPath();
  corners.forEach((c, i) => {
    if (i === 0) ctx.moveTo(c.x, c.y);
    else ctx.lineTo(c.x, c.y);
  });
  ctx.closePath();
  ctx.fillStyle = fillStyle;
  ctx.fill();
}
function getStallScreenCorners(s: ParkingSlot): Point[] {
  return orientedRectCorners(s).map(lmToScreen);
}
function drawTandemDividers(ctx: CanvasRenderingContext2D, slots: ParkingSlot[]): void {
  slots.forEach((s) => {
    if (s.tandemPosition !== 'front') return;
    const w = s.width / 2;
    const d = s.depth / 2;
    const p1 = addP(rotP({ x: -w, y: d }, s.angle), s.center);
    const p2 = addP(rotP({ x: w, y: d }, s.angle), s.center);
    const sp1 = lmToScreen(p1);
    const sp2 = lmToScreen(p2);
    ctx.save();
    ctx.setLineDash([4, 3]);
    ctx.strokeStyle = '#ffd43b';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(sp1.x, sp1.y);
    ctx.lineTo(sp2.x, sp2.y);
    ctx.stroke();
    ctx.restore();
  });
}
function drawZonePavement(ctx: CanvasRenderingContext2D, zone: OrientedRect, color: string): void {
  ctx.beginPath();
  const corners = orientedRectCorners(zone).map(lmToScreen);
  corners.forEach((c, i) => {
    if (i === 0) ctx.moveTo(c.x, c.y);
    else ctx.lineTo(c.x, c.y);
  });
  ctx.closePath();
  state.buildings.forEach((b) => {
    const bc = getBuildingScreenCorners(b);
    bc.forEach((c, i) => {
      if (i === 0) ctx.moveTo(c.x, c.y);
      else ctx.lineTo(c.x, c.y);
    });
    ctx.closePath();
  });
  ctx.fillStyle = hexToRgba(color, 0.55);
  ctx.fill('evenodd');
}
function drawZoneOutline(ctx: CanvasRenderingContext2D, zone: OrientedRect, color: string): void {
  const corners = orientedRectCorners(zone).map(lmToScreen);
  ctx.beginPath();
  corners.forEach((c, i) => {
    if (i === 0) ctx.moveTo(c.x, c.y);
    else ctx.lineTo(c.x, c.y);
  });
  ctx.closePath();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.setLineDash([6, 4]);
  ctx.stroke();
  ctx.setLineDash([]);
}
// 背合わせ(pair)・壁背負い(wall)の列の前にある通路帯を可視化する。隣り合う帯の奥側/手前側の通路は同じ場所に
// 重なるため、全帯を1つのパスにまとめて1回で塗り(重なりで濃く見えないように)、センターラインも重複を除いて引く
function drawLaneAisles(ctx: CanvasRenderingContext2D, lanes: ParkingLane[]): void {
  const rects = lanes.flatMap(laneAisleRects);
  if (rects.length === 0) return;
  ctx.beginPath();
  for (const rect of rects) {
    orientedRectCorners(rect)
      .map(lmToScreen)
      .forEach((c, i) => {
        if (i === 0) ctx.moveTo(c.x, c.y);
        else ctx.lineTo(c.x, c.y);
      });
    ctx.closePath();
  }
  ctx.fillStyle = 'rgba(50,54,60,0.22)';
  ctx.fill('nonzero');
  const seen = new Set<string>();
  ctx.save();
  ctx.setLineDash([6, 5]);
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth = 1.5;
  for (const rect of rects) {
    const key = `${rect.center.x.toFixed(2)},${rect.center.y.toFixed(2)},${rect.width.toFixed(2)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const halfW = rect.width / 2;
    const sp1 = lmToScreen(addP(rect.center, rotP({ x: -halfW, y: 0 }, rect.angle)));
    const sp2 = lmToScreen(addP(rect.center, rotP({ x: halfW, y: 0 }, rect.angle)));
    ctx.beginPath();
    ctx.moveTo(sp1.x, sp1.y);
    ctx.lineTo(sp2.x, sp2.y);
    ctx.stroke();
  }
  ctx.restore();
}
// 「画面全体の矩形」から「各建物の輪郭」をくり抜いた領域をクリップ範囲に設定する(evenodd規則)。
// 建物は半透明で駐車場レイヤーの後に描画されるため、これを使わないと通路の塗り・線が
// 建物の下から透けて見えてしまう。この関数呼び出し後に描画したものだけが建物の外側に限定される
function clipOutBuildings(ctx: CanvasRenderingContext2D): void {
  ctx.beginPath();
  ctx.rect(-10000, -10000, 20000, 20000);
  state.buildings.forEach((b) => {
    const corners = getBuildingScreenCorners(b);
    corners.forEach((c, i) => {
      if (i === 0) ctx.moveTo(c.x, c.y);
      else ctx.lineTo(c.x, c.y);
    });
    ctx.closePath();
  });
  ctx.clip('evenodd');
}
function drawParkingSlots(ctx: CanvasRenderingContext2D): void {
  const lanes = state.parking.lanes || [];

  /* 1. 出入口に接続する通路領域(点線センターラインで出入口からの動線を明示)。建物と重なる部分は表示しない */
  ctx.save();
  clipOutBuildings(ctx);
  (state.parking.drivewayConnectors || []).forEach((c: DrivewayConnector) => {
    fillScreenPoly(ctx, orientedRectCorners(c).map(lmToScreen), hexToRgba(PARKING_PAVEMENT, 0.8));
    const halfD = c.width / 2;
    const p1 = addP(c.center, rotP({ x: -halfD, y: 0 }, c.angle));
    const p2 = addP(c.center, rotP({ x: halfD, y: 0 }, c.angle));
    const sp1 = lmToScreen(p1);
    const sp2 = lmToScreen(p2);
    ctx.save();
    ctx.setLineDash([6, 5]);
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(sp1.x, sp1.y);
    ctx.lineTo(sp2.x, sp2.y);
    ctx.stroke();
    ctx.restore();
  });
  ctx.restore();

  /* 2. 各配置方式のゾーン舗装(方式ごとに色分け。背合わせは敷地領域と同一のため表示しない) */
  PARKING_METHOD_KEYS.forEach((key) => {
    if (key === 'backToBack') return;
    const m = state.parking.methods[key];
    if (m.enabled && m.zone) drawZonePavement(ctx, m.zone, PARKING_METHOD_COLORS[key]);
  });

  /* 2.5 背合わせ・壁背負いの通路帯(点線センターラインで可視化)。建物と重なる部分は表示しない */
  ctx.save();
  clipOutBuildings(ctx);
  drawLaneAisles(ctx, lanes);
  ctx.restore();

  /* 3. 駐車マス(白線区切り。特殊タイプのみ色付きで塗る) */
  state.parking.generatedSlots.forEach((s) => {
    const corners = getStallScreenCorners(s);
    const fill = PARKING_COLORS[s.type];
    if (fill) fillScreenPoly(ctx, corners, fill);
    ctx.beginPath();
    corners.forEach((c, i) => {
      if (i === 0) ctx.moveTo(c.x, c.y);
      else ctx.lineTo(c.x, c.y);
    });
    ctx.closePath();
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.lineWidth = 1.2;
    ctx.stroke();
  });

  /* 5. タンデム区切り線 */
  drawTandemDividers(ctx, state.parking.generatedSlots);

  /* 6. 各配置方式ゾーンの枠線(選択中はハンドル表示。背合わせは敷地領域と同一のため表示しない) */
  PARKING_METHOD_KEYS.forEach((key) => {
    if (key === 'backToBack') return;
    const m = state.parking.methods[key];
    if (!m.enabled || !m.zone) return;
    drawZoneOutline(ctx, m.zone, PARKING_METHOD_COLORS[key]);
    if (state.ui.selectedId === parkingZoneId(key)) drawHandles(ctx, m.zone);
  });

  if (state.bicycle.enabled && state.bicycle.zone) {
    const corners = orientedRectCorners(state.bicycle.zone).map(lmToScreen);
    ctx.beginPath();
    corners.forEach((c, i) => {
      if (i === 0) ctx.moveTo(c.x, c.y);
      else ctx.lineTo(c.x, c.y);
    });
    ctx.closePath();
    ctx.fillStyle = 'rgba(255,217,236,0.5)';
    ctx.strokeStyle = 'rgba(214,140,180,0.85)';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([5, 4]);
    ctx.fill();
    ctx.stroke();
    ctx.setLineDash([]);
    if (state.ui.selectedId === BICYCLE_ZONE_ID) drawHandles(ctx, state.bicycle.zone);
  }
}

function drawAllShadows(ctx: CanvasRenderingContext2D): void {
  const positions = getSunPositions();
  if (positions.size === 0) return;
  state.shadowCheck.hours.forEach((hour) => {
    const sunPos = positions.get(hour) ?? null;
    const color = shadowColorForHour(hour);
    state.buildings.forEach((b) => {
      const poly = computeShadowPolygonLm(b, sunPos);
      if (!poly || poly.length < 3) return;
      const screenPts = poly.map(lmToScreen);
      ctx.beginPath();
      screenPts.forEach((p, i) => {
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.closePath();
      ctx.fillStyle = hexToRgba(color, 0.22);
      ctx.strokeStyle = hexToRgba(color, 0.65);
      ctx.lineWidth = 1.2;
      ctx.fill();
      ctx.stroke();
    });
  });
}

export function redrawBuildingsOverlay(): void {
  if (!buildingCtx || !buildingCanvas) return;
  const dpr = getCanvasDpr();
  const ctx = buildingCtx;
  ctx.save();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, buildingCanvas.width / dpr, buildingCanvas.height / dpr);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  if (state.site.origin) {
    drawParkingSlots(ctx);
    if (state.shadowCheck.enabled) drawAllShadows(ctx);
    const slopeCheckCache = getSlopeCheckCache();
    state.buildings.forEach((b) => {
      drawBuilding(ctx, b, b.id === state.ui.selectedId, slopeCheckCache.get(b.id));
    });
  }
  ctx.restore();
}
